import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  formatSerpText,
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 중간관리자 교체 정산 — 레거시 appr_edit_forms_serp_shop_change_manager */
export const SERP_INTERMEDIARY_REPLACE_TYPE = 182;

export const isSerpIntermediaryReplaceType = (apvType: number): boolean =>
  apvType === SERP_INTERMEDIARY_REPLACE_TYPE;

export type SerpIntermediaryInspectionRow = {
  SSN_CD?: unknown;
  STD_STK_QTY?: unknown;
  SALE_AMT?: unknown;
  STCKT_QTY?: unknown;
  STCKT_AMT?: unknown;
  GAP_QTY?: unknown;
  GAP_AMT?: unknown;
  LOSS_AMT?: unknown;
};

export type SerpIntermediarySettlementRow = {
  SSN_CD?: unknown;
  LOSS_AMT?: unknown;
  GI_RAT?: unknown;
  DED_RTO?: unknown;
  STMT_AMT?: unknown;
  APRV_RMK?: unknown;
};

/** EAI 응답 원문 — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type SerpIntermediaryReplaceSource = {
  BRAND_NM?: unknown;
  SHOP_NM?: unknown;
  STK_INVES_LIST?: unknown;
  STMT_LIST?: unknown;
  [key: string]: unknown;
};

export type SerpIntermediaryReplaceDraftSource = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: SerpIntermediaryReplaceSource | null };
};

export type SerpIntermediaryReplaceState = SerpDocumentRef & {
  source: SerpIntermediaryReplaceSource;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교. 편집은 미사용 */
  autoSubject: string;
};

const asRows = <T>(raw: unknown): T[] => (Array.isArray(raw) ? raw : []);

export const getSerpIntermediaryInspectionRows = (
  source: SerpIntermediaryReplaceSource,
) => asRows<SerpIntermediaryInspectionRow>(source.STK_INVES_LIST);

export const getSerpIntermediarySettlementRows = (
  source: SerpIntermediaryReplaceSource,
) => asRows<SerpIntermediarySettlementRow>(source.STMT_LIST);

export const buildSerpIntermediaryReplaceSubject = (
  source: SerpIntermediaryReplaceSource,
  t: TFunction,
): string =>
  t('{{brand}} {{shop}} 중간관리자 교체 정산의 건', {
    brand: formatSerpText(source.BRAND_NM),
    shop: formatSerpText(source.SHOP_NM),
    interpolation: { escapeValue: false },
  });

export const checkSerpIntermediaryReplaceBeforeSave = (
  state: SerpIntermediaryReplaceState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

export const buildSerpIntermediaryReplaceAttributes = (
  state: SerpIntermediaryReplaceState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: state.source,
      }
    : null;
